import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Instagram, Menu, X, ChevronDown } from "lucide-react";
import { useSchedule } from "@/hooks/use-schedule";
import { Link, useLocation } from "@tanstack/react-router";
import logo from "@/assets/logo-clara-camille.png";

const servicosLinks = [
  { label: "Consulta online", href: "/consulta-nutricional-online-australia", desc: "Atendimento por videochamada" },
  { label: "Acompanhamento", href: "/acompanhamento-nutricional-australia", desc: "Plano e suporte contínuo" },
  { label: "Primeira consulta", href: "/consulta-inicial-gratuita", desc: "Como funciona a consulta inicial" },
];

const Navigation = () => {
  const { openDialog } = useSchedule();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicosOpen, setMobileServicosOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isServicosActive = servicosLinks.some((l) => location.pathname === l.href);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
          {/* Left side: logo (desktop) / hamburger + instagram (mobile) */}
          <Link to="/" className="hidden lg:flex items-center">
            <img src={logo} alt="Clara Camille" className="h-14 sm:h-16 w-auto" width={177} height={140} />
          </Link>

          <div className="flex items-center gap-2 lg:hidden">
            <button
              className="text-foreground p-1"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <a
              href="https://www.instagram.com/nutriclaracamille_/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:opacity-70 transition-opacity"
              aria-label="Instagram da Clara Camille"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-sans transition-all ${
                location.pathname === "/"
                  ? "gradient-text italic font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Início
            </Link>

            <Link
              to="/sobre"
              className={`px-4 py-2 rounded-full text-sm font-sans transition-all ${
                location.pathname === "/sobre"
                  ? "gradient-text italic font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Sobre
            </Link>

            {/* Serviços dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-sans transition-all ${
                  isServicosActive
                    ? "gradient-text italic font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
                }`}
              >
                Serviços
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {dropdownOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 rounded-2xl border border-border bg-background shadow-xl z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150"
                >
                  <div className="p-2">
                    {servicosLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={`block rounded-xl px-4 py-3 transition-colors ${
                          location.pathname === link.href
                            ? "bg-primary/10"
                            : "hover:bg-primary/5"
                        }`}
                      >
                        <span
                          className={`block text-sm font-semibold font-sans ${
                            location.pathname === link.href
                              ? "gradient-text italic"
                              : "text-foreground"
                          }`}
                        >
                          {link.label}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {link.desc}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/blog"
              className={`px-4 py-2 rounded-full text-sm font-sans transition-all ${
                location.pathname.startsWith("/blog")
                  ? "gradient-text italic font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Blog
            </Link>

            <Link
              to="/contato"
              className={`px-4 py-2 rounded-full text-sm font-sans transition-all ${
                location.pathname === "/contato"
                  ? "gradient-text italic font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Contato
            </Link>
          </div>

          {/* Right side: agendar (mobile + desktop) + instagram (desktop) */}
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="https://www.instagram.com/nutriclaracamille_/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:opacity-70 transition-opacity hidden lg:block"
              aria-label="Instagram da Clara Camille"
            >
              <Instagram className="h-5 w-5" />
            </a>
            <Button
              variant="gradient"
              className="rounded-full text-xs sm:text-sm px-4 sm:px-6"
              style={{ background: "var(--gradient-green)" }}
              onClick={openDialog}
            >
              Agendar consulta
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background backdrop-blur-md animate-in slide-in-from-top-2 duration-200">
          <div className="container mx-auto px-4 py-3 flex flex-col">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className={`py-3 px-3 rounded-xl text-sm font-sans transition-colors ${
                location.pathname === "/"
                  ? "gradient-text italic font-semibold bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Início
            </Link>

            <Link
              to="/sobre"
              onClick={() => setMobileOpen(false)}
              className={`py-3 px-3 rounded-xl text-sm font-sans transition-colors ${
                location.pathname === "/sobre"
                  ? "gradient-text italic font-semibold bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Sobre
            </Link>

            {/* Mobile serviços accordion */}
            <button
              onClick={() => setMobileServicosOpen(!mobileServicosOpen)}
              className={`flex items-center justify-between py-3 px-3 rounded-xl text-sm font-sans transition-colors ${
                isServicosActive && !mobileServicosOpen
                  ? "gradient-text italic font-semibold bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Serviços
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${mobileServicosOpen ? "rotate-180" : ""}`}
              />
            </button>

            {mobileServicosOpen && (
              <div className="ml-3 border-l-2 border-primary/20 pl-3 flex flex-col gap-1 animate-in slide-in-from-top-1 duration-150">
                {servicosLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`py-2.5 px-3 rounded-xl text-sm font-sans transition-colors ${
                      location.pathname === link.href
                        ? "gradient-text italic font-semibold bg-primary/5"
                        : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
                    }`}
                  >
                    <span className="block">{link.label}</span>
                    <span className="block text-xs text-muted-foreground/70 mt-0.5">{link.desc}</span>
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/blog"
              onClick={() => setMobileOpen(false)}
              className={`py-3 px-3 rounded-xl text-sm font-sans transition-colors ${
                location.pathname.startsWith("/blog")
                  ? "gradient-text italic font-semibold bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Blog
            </Link>

            <Link
              to="/contato"
              onClick={() => setMobileOpen(false)}
              className={`py-3 px-3 rounded-xl text-sm font-sans transition-colors ${
                location.pathname === "/contato"
                  ? "gradient-text italic font-semibold bg-primary/5"
                  : "text-muted-foreground hover:text-foreground hover:bg-primary/5"
              }`}
            >
              Contato
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
